import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { WebSocketEventType } from "@/model";
import { useScenarioId, useModal, useWebsocket } from "@/hook";
import Button from "@/component/Button";
import { TextInputGroup } from "@/component/input";
import { successToast, warningToast } from "@/component/Toast";
import { Modal, UploadModalTitle, FileUploadZone } from "@/component/modal";


export const TEST_UPLOAD_MODAL_ID = "test-upload-modal-id";

const TestUploadModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();
    const { requestBulkTest, addEventListener, removeEventListener } = useWebsocket();

    const [file, setFile] = useState<File | null>(null);
    const [title, setTitle] = useState<string>();
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const onClickCancel = () => {
        setFile(null);
        modal.close(TEST_UPLOAD_MODAL_ID);
    };

    const onClickConfirm = () => {
        if (!file) {
            warningToast(intl.formatMessage({ id: "i05013" }));
            return;
        }
        if (!title) {
            warningToast(intl.formatMessage({ id: "i05014" }));
            return;
        }

        try {
            requestBulkTest(scenarioId, file, title);
        } catch (error) {
            warningToast(intl.formatMessage({ id: "i05015" }));
            return;
        }

        successToast(intl.formatMessage({ id: "i00007" }));
        setFile(null);
        modal.close(TEST_UPLOAD_MODAL_ID);
    };

    const onClose = () => {
        setFile(null);
    };

    useEffect(() => {
        const listeners: { event: WebSocketEventType, handler: () => void }[] = [
            { event: "bulk_test_started", handler: () => setIsUploading(true) },
            { event: "bulk_test_progress", handler: () => setIsUploading(true) },
            { event: "bulk_test_finished", handler: () => setIsUploading(false) },
            { event: "bulk_test_canceled", handler: () => setIsUploading(false) }
        ];

        listeners.forEach(({ event, handler }) => {
            addEventListener(event, handler);
        });

        return () => {
            listeners.forEach(({ event, handler }) => {
                removeEventListener(event, handler);
            });
        };
    }, [addEventListener, removeEventListener]);

    return (
        <Modal id={TEST_UPLOAD_MODAL_ID}
               title={
                   <UploadModalTitle label={intl.formatMessage({ id: "b0004" })}
                                     sampleFileName="cm_bulk_test_sample"
                                     sampleFileColumns={[
                                         { attribute: intl.formatMessage({ id: "i05008" }) },
                                     ]}
                   />
                }
                onClose={onClose}
        >
            <div className="modal-body w-[780px]">
                <fieldset>
                    <legend className="sr-only">
                        {intl.formatMessage({ id: "b0004" })}
                    </legend>
                    <TextInputGroup required
                                    useDirty
                                    rowGap={8}
                                    aria-label={intl.formatMessage({ id: "i05002" })}
                                    placeholder={intl.formatMessage({ id: "i05009" })}
                                    onChange={setTitle}
                    />

                    <FileUploadZone file={file}
                                    onChangeFile={setFile}
                    />
                </fieldset>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={isUploading}
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isUploading || !title?.trim() || !file}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default TestUploadModal;
